<template>
    <div class="revisions-manager">
        <main-dialog 
            v-model="showModal" 
            size="xl"
            :title="modalTitle"
            hide-footer
            scrollable
        >
            <RevisionsList 
                v-if="currentView === 'list'"
                :component-uuid="componentUuid"
                :model-uuid="modelUuid"
                :current-version="currentVersion"
                :current-model="currentModel"
                :can-restore="canRestore"
                @view="handleViewRevision"
                @restore="handleRestoreRevision"
                @compare="handleCompareRevisions"
            />

            <div v-else-if="currentView === 'view'" class="revision-view">
                <div class="mb-3">
                    <b-button variant="link" @click="backToList">
                        <b-icon icon="arrow-left" class="mr-1" />
                        {{ $t('backToRevisions') }}
                    </b-button>
                </div>
                
                <h5 class="mb-3">
                    {{ $t('revisionDetails', { version: viewingRevision.__version__ }) }}
                </h5>
                
                <b-row class="mb-3">
                    <b-col md="6">
                        <strong>{{ $t('version') }}:</strong> {{ viewingRevision.__version__ }}
                    </b-col>
                    <b-col md="6">
                        <strong>{{ $t('updatedAt') }}:</strong> {{ $toUserTimeZone(viewingRevision.updated_at) }}
                    </b-col>
                    <b-col md="6" class="mt-2" v-if="viewingRevision.updated_by">
                        <strong>{{ $t('updatedBy') }}:</strong> {{ viewingRevision.updated_by }}
                    </b-col>
                </b-row>

                <div class="revision-data">
                    <h6>{{ $t('data') }}</h6>
                    <pre class="revision-json">{{ formatRevisionData(viewingRevision) }}</pre>
                </div>

                <div class="mt-3" v-if="canRestore && !isCurrentVersion(viewingRevision)">
                    <b-button variant="warning" @click="handleRestoreRevision(viewingRevision)">
                        <b-icon icon="arrow-counterclockwise" class="mr-1" />
                        {{ $t('restoreThisRevision') }}
                    </b-button>
                </div>
            </div>

            <div v-else-if="currentView === 'compare'" class="revision-compare">
                <div class="mb-3">
                    <b-button variant="link" @click="backToList">
                        <b-icon icon="arrow-left" class="mr-1" />
                        {{ $t('backToRevisions') }}
                    </b-button>
                </div>

                <h5 class="mb-3">
                    {{ $t('comparingRevisions') }}
                </h5>

                <DiffViewer 
                    :left-revision="compareData.left"
                    :right-revision="compareData.right"
                    :fields="fields"
                />
            </div>
        </main-dialog>
    </div>
</template>

<script>
import RevisionsList from './RevisionsList.vue'
import DiffViewer from './DiffViewer.vue'

export default {
    name: 'RevisionsManager',
    components: {
        RevisionsList,
        DiffViewer
    },
    props: {
        componentUuid: {
            type: String,
            required: true
        },
        modelUuid: {
            type: String,
            required: true
        },
        currentVersion: {
            type: Number,
            default: null
        },
        currentModel: {
            type: Object,
            default: null
        },
        fields: {
            type: Array,
            default: () => []
        },
        canRestore: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showModal: false,
            currentView: 'list', // 'list', 'view', 'compare'
            viewingRevision: null,
            compareData: {
                left: null,
                right: null
            }
        }
    },
    computed: {
        modalTitle() {
            if (this.currentView === 'list') {
                return this.$t('revisionHistory')
            } else if (this.currentView === 'view') {
                return this.$t('viewRevision')
            } else if (this.currentView === 'compare') {
                return this.$t('compareRevisions', { 
                    v1: (this.compareData.left && this.compareData.left.__version__) || '', 
                    v2: (this.compareData.right && this.compareData.right.__version__) || ''
                })
            }
            return ''
        }
    },
    methods: {
        open() {
            this.showModal = true
            this.currentView = 'list'
        },

        close() {
            this.showModal = false
            this.currentView = 'list'
            this.viewingRevision = null
            this.compareData = { left: null, right: null }
        },

        backToList() {
            this.currentView = 'list'
            this.viewingRevision = null
            this.compareData = { left: null, right: null }
        },

        handleViewRevision(revision) {
            this.viewingRevision = revision
            this.currentView = 'view'
        },

        handleRestoreRevision(revision) {
            this.$emit('restore', revision)
            this.close()
        },

        handleCompareRevisions({ left, right }) {
            this.compareData = { left, right }
            this.currentView = 'compare'
        },

        isCurrentVersion(revision) {
            return this.currentVersion && revision.__version__ === this.currentVersion
        },

        formatRevisionData(revision) {
            const systemFields = ['__version__', '__build__', '__hot_build__']
            const data = { ...revision }
            
            systemFields.forEach(field => {
                delete data[field]
            })
            
            return JSON.stringify(data, null, 2)
        }
    }
}
</script>

<style lang="scss" scoped>
.revision-view {
    .revision-json {
        background-color: var(--bg-secondary);
        color: var(--text-primary);
        padding: 1rem;
        border-radius: 4px;
        border: 1px solid var(--border-default);
        max-height: 400px;
        overflow-y: auto;
        font-size: 0.85rem;
        font-family: 'Courier New', monospace;
    }
}

.revision-data {
    h6 {
        font-weight: 600;
        margin-bottom: 0.5rem;
    }
}
</style>
